import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, SelectF, CheckF, fmtDate } from "./AFields";
import { Modal } from "./Participants";

export default function Users() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (editing.id) await api.put(`/admin/users/${editing.id}`, editing);
      else await api.post("/admin/users", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-users">
      <SEO title="Users" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Users</h1>
          <p className="mt-1 text-sm text-slate-500">Manage Super Admin and authorised Greeting users.</p>
        </div>
        <button onClick={() => setEditing({ role: "greeting", active: true })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="user-new-btn"><Plus size={16} /> New User</button>
      </div>
      <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Active</th><th className="px-4 py-3">Created</th><th className="px-4 py-3 text-right">Edit</th></tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-b border-gray-100 last:border-0" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-semibold text-navy">{u.name || "—"}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${u.role === "admin" ? "bg-navy text-white" : "bg-saffron-100 text-saffron-600"}`}>{u.role}</span></td>
                <td className="px-4 py-3">{u.active ? "Yes" : "No"}</td>
                <td className="px-4 py-3">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing({ ...u, password: "" })} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-bold text-navy hover:border-saffron-500" data-testid={`user-edit-${u.id}`}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? `Edit — ${editing.email}` : "New User"} testid="user-editor">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
            {!editing.id && <TextF label="Email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} />}
            <SelectF label="Role" value={editing.role} onChange={(v) => setEditing({ ...editing, role: v })} options={["greeting", "admin"]} />
            <TextF label={editing.id ? "New Password (leave blank to keep)" : "Password (min 8 chars)"} type="password" value={editing.password} onChange={(v) => setEditing({ ...editing, password: v })} />
            {editing.id && <CheckF label="Active" value={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="user-save">{busy && <Loader2 size={15} className="animate-spin" />} Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
